import axios from "../AxiosInterCeptor/AxiosInterCeptor";
import { ToastError, ToastSuccess } from "../UiComponents/Toaster/Toast";

const URL = process.env.React_App_BaseUrl;

const GetPolicyWalletList = () => {
  return axios
    .get(`${URL}/policyWallet`)
    .then((response) => response.data)
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to fetch policy wallet requests";
      ToastError(message);
      return { error: true, message };
    });
};

// Used by the Motor Policy creation flow to gate a USER login's create
// against their admin-approved Policy Wallet balance. Silent on failure —
// treated as "nothing approved" rather than blocking the form on a
// transient error; the create-time check is the authoritative one.
const GetPolicyWalletBalance = () => {
  return axios
    .get(`${URL}/policyWallet/balance`)
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
      return { error: true, balance: 0 };
    });
};

const CreatePolicyWalletTopup = (formData) => {
  return axios
    .post(`${URL}/policyWallet/topup`, formData)
    .then((response) => {
      const res = response.data;
      if (!res.error) {
        ToastSuccess(res.message || "Topup request submitted successfully");
      }
      return res;
    })
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to submit topup request";
      ToastError(message);
      return { error: true, message };
    });
};

const UpdatePolicyWalletApproval = (id, status) => {
  return axios
    .patch(`${URL}/policyWallet/${id}/${status}`)
    .then((response) => {
      const res = response.data;
      if (!res.error) {
        ToastSuccess(res.message || "Status updated successfully");
      }
      return res;
    })
    .catch((error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to update status";
      ToastError(message);
      return { error: true, message };
    });
};

// Admin "add money to a user's wallet" (CLIENT only). Unlike the calls above
// these never toast: the Add Money page renders errors inline. Failures
// resolve to {error, message, code, noResponse}: `code` is the backend's
// machine-readable reason, and `noResponse` marks "nothing usable came back"
// (network error, timeout, empty body, gateway 502/503/504), which is the
// only case where a retry may reuse the same idempotency key.
const request = (call, fallback) =>
  call
    .then((response) => response.data || { error: true, message: "Empty response", noResponse: true })
    .catch((error) => {
      const status = error?.response?.status;
      return {
        error: true,
        message: error?.response?.data?.message || error?.message || fallback,
        code: error?.response?.data?.code,
        noResponse: !error?.response || [502, 503, 504].includes(status),
      };
    });

// The super admin also gets `admins` (every admin with user logins) and may narrow the users with { adminId }.
const GetPolicyWalletEligibleUsers = (params) =>
  request(axios.get(`${URL}/policyWallet/eligibleUsers`, params ? { params } : undefined), "Could not load users");

const GetPolicyWalletBalanceForUser = (userId) =>
  request(
    axios.get(`${URL}/policyWallet/balance`, { params: { userId } }),
    "Could not load the balance"
  );

const PostPolicyWalletAddMoney = (formData) =>
  request(axios.post(`${URL}/policyWallet/addMoney`, formData), "Could not reach the server");

const GetPolicyWalletAdminTopups = (params) =>
  request(
    axios.get(`${URL}/policyWallet/adminTopups`, { params }),
    "Could not load recent additions"
  );

// Admin overview (CLIENT and accountant, read-only): one row per user with
// balance / pending / total added / total used, and one user's ledger, a page
// (1-based) at a time.
const GetPolicyWalletSummary = () =>
  request(axios.get(`${URL}/policyWallet/summary`), "Could not load the user wallets");

const GetPolicyWalletStatement = (userId, { page = 1, limit = 25 } = {}) =>
  request(
    axios.get(`${URL}/policyWallet/statement`, { params: { userId, page, limit } }),
    "Could not load the statement"
  );

// The signed-in user's own statement (the "user" login's Policy Wallet page): money added, policy debits
// and refunds, with the balance after each. The server answers only for the caller.
const GetMyPolicyWalletStatement = ({ page = 1, limit = 25 } = {}) =>
  request(axios.get(`${URL}/policyWallet/myStatement`, { params: { page, limit } }), "Could not load your statement");

// The signed-in "user" sets THEIR OWN low-balance threshold (0 turns it off); the navbar bell warns while the balance
// is at or below it. The server only ever changes the wallet of the caller. Resolves to { error, message, data }.
const PutPolicyWalletMinimum = (minimumBalance) =>
  request(axios.put(`${URL}/policyWallet/minimum`, { minimumBalance }), "Could not save the minimum balance");

// The navbar bell: low-balance alerts worked out by the server from the caller's own wallets. Never toasts; the bell
// shows its own state. Resolves to { error:false, data:{ alerts, count } } or { error:true, message }.
const GetWalletAlerts = () => request(axios.get(`${URL}/walletAlerts`), "Could not load the alerts");

export {
  PutPolicyWalletMinimum,
  GetWalletAlerts,
  GetPolicyWalletSummary,
  GetPolicyWalletStatement,
  GetMyPolicyWalletStatement,
  GetPolicyWalletList,
  GetPolicyWalletBalance,
  CreatePolicyWalletTopup,
  UpdatePolicyWalletApproval,
  GetPolicyWalletEligibleUsers,
  GetPolicyWalletBalanceForUser,
  PostPolicyWalletAddMoney,
  GetPolicyWalletAdminTopups,
};
